import { isAfter, isBefore, isValid } from 'date-fns'
import * as R from 'ramda'
import {
  type AppointmentEventType,
  moment,
  type NamedEntity,
  type Nil,
} from '@pbt/pbt-ui-components'

import { AppointmentStateColorMap } from '~/constants/event'
import { SlotType } from '~/constants/schedulerConstants'
import i18n from '~/locales/i18n'
import type {
  BoardingSchedule,
  TimetableColumnLayout,
  TimetableDateRange,
  TimetableEvent,
  TimetableFilter,
  TimetableOverlapGroup,
  TimetableSlot,
  TimetableSlotBoundingShape,
  TimetableSlotBounds,
  TimetableSlotPosition,
} from '~/types'
import { getUrlSearchParam } from '~/utils'
import { formatHourRange } from '~/utils/time'

export const getSlotId = (slot: TimetableSlot) =>
  slot.appointment || slot.busyTime

export const dragDropIdToReal = (dragDropId: string) => dragDropId.split('-')[0]
export const realIdToDragDrop = (id: string, type: string) =>
  type ? `${id}-${type}` : id

export const getFilterGroupsBySelectedIds = (
  defaultFilters: TimetableFilter<NamedEntity>[],
  selectedLabels: string[],
) =>
  selectedLabels?.length
    ? defaultFilters.map((item) => ({
        ...item,
        selected: selectedLabels.includes(item.id),
      }))
    : defaultFilters

export const getSlotIdForPlaceholder = (
  slots: TimetableSlot[],
  appointment: TimetableEvent | undefined,
  appointmentsMap: Record<string, TimetableEvent>,
) => {
  if (!appointment) {
    return undefined
  }

  const slot = slots.find((s) => {
    const id = getSlotId(s)
    const slotAppointment = id ? appointmentsMap[id] : undefined
    return moment(slotAppointment?.scheduledStartDatetime).isAfter(
      moment(appointment.scheduledStartDatetime),
    )
  })

  return slot && getSlotId(slot)
}

export const getSlotsAreOverlapping = (
  slotOne: TimetableSlot,
  slotTwo: TimetableSlot,
) => {
  const notOverlapping =
    moment(slotOne.interval.to).isSameOrBefore(slotTwo.interval.from) ||
    moment(slotTwo.interval.to).isSameOrBefore(slotOne.interval.from)

  return !notOverlapping
}

export const getSlotsAreOverlappingAndSameType = (
  slotOne: TimetableSlot,
  slotTwo: TimetableSlot,
) => {
  const notOverlapping =
    moment(slotOne.interval.to).isSameOrBefore(slotTwo.interval.from) ||
    moment(slotTwo.interval.to).isSameOrBefore(slotOne.interval.from) ||
    // if one slot is Busy type, then it should not count as overlapping with a non-Busy slot
    (slotOne.type === SlotType.BUSY) !== (slotTwo.type === SlotType.BUSY)

  return !notOverlapping
}

/**
 * Checks if a slot can be placed in the same column/subcolumn as the other slots
 * in the column, or if it needs to be placed in a different column/subcolumn.
 * This is the most basic form of the check, only caring about overlap status.
 * @param column The slots within the same column/subcolumns
 * @param newSlot The candidate slot to be added to the column/subcolumn
 * @return true if it can fit, false if it should go elsewhere
 */
const canFitSlotIntoColumn = (
  column: TimetableSlot[],
  newSlot: TimetableSlot,
) => column.every((slot) => !getSlotsAreOverlapping(slot, newSlot))

/**
 * Checks if a slot can be placed in the same column/subcolumn as the other slots
 * in the column, or if it needs to be placed in a different column/subcolumn.
 * This one determines eligibility based on SlotType, allowing certain overlaps
 * to fit into the same column/subcolumn if their types are correct.
 * @param column The slots within the same column/subcolumns
 * @param newSlot The candidate slot to be added to the column/subcolumn
 * @return true if it can fit, false if it should go elsewhere
 */
const canFitSlotIntoColumnByType = (
  column: TimetableSlot[],
  newSlot: TimetableSlot,
) => column.every((slot) => !getSlotsAreOverlappingAndSameType(slot, newSlot))

export const isAppointmentSlot = (slot: TimetableSlot) =>
  slot.type === SlotType.APPOINTMENT

const isBusySlot = (slot: TimetableSlot) => slot.type === SlotType.BUSY

/**
 * Determines if a BUSY slot is a reserved slot using TimetableEvent data.
 * For CVC-34823, replace eventMap/reservedEventId with a RESERVED SlotType.
 */
export const isBusySlotReserved = (
  slot: TimetableSlot,
  eventMap: Record<string, TimetableEvent> | undefined,
  reservedEventId: string | undefined,
): boolean => {
  if (!slot || !eventMap || !reservedEventId) return false
  const slotId = getSlotId(slot)
  return Boolean(
    isBusySlot(slot) &&
    slotId &&
    reservedEventId &&
    eventMap[slotId]?.typeId === reservedEventId,
  )
}

/**
 * Updated version of canFitSlotIntoColumnByType, which differentiates its logic
 * for BUSY slots if it's a reserved slot. Reserved slots can be placed under
 * APPOINTMENT slots, but other BUSY slots cannot.
 *
 * eventMap & reservedEventId should be retired as part of CVC-34823
 *
 * @param column The slots within the same column/subcolumns
 * @param newSlot The candidate slot to be added to the column/subcolumn
 * @param eventMap A mapping of slot IDs to their respective TimetableEvent
 * @param reservedEventId The event type ID for reserved slots
 * @return true if it can fit, false if it should go elsewhere
 */
export const canFitSlotIntoColumnByTypeV2 = (
  column: TimetableSlot[],
  newSlot: TimetableSlot,
  eventMap: Record<string, TimetableEvent>,
  reservedEventId: string,
) =>
  column.every((slot) => {
    const slotsOverlap = getSlotsAreOverlapping(slot, newSlot)
    const oneSlotIsBusy = isBusySlot(slot) !== isBusySlot(newSlot)
    // If slot is BUSY with Reserved type, APPOINTMENT slots should overlap (same position)
    // If slot is other BUSY type, APPOINTMENT slots should be side-by-side (different positions)
    if (slotsOverlap && oneSlotIsBusy) {
      return (
        (isBusySlotReserved(slot, eventMap, reservedEventId) &&
          isAppointmentSlot(newSlot)) ||
        (isAppointmentSlot(slot) &&
          isBusySlotReserved(newSlot, eventMap, reservedEventId))
      )
    }
    return !getSlotsAreOverlappingAndSameType(slot, newSlot)
  })

/**
 * Greedy packing algorithm that recursively divides a list of slots into
 * columns/subcolumns. Uses canFitSlotIntoColumn to determine if overlapping
 * slots can be in the same column/subcolumn as one another.
 * @param slots Slots to be assigned to a column/subcolumn of the timetable.
 * @param columns The current column/subcolumns that have already been defined.
 *                It is used to persist column/subcolumn state during recursion;
 *                An empty array is typically used as the start value.
 * @return A 2D slot array, where each inner array is a column/subcolumn's slots.
 */
export const getSlotsPositions = (
  slots: TimetableSlot[] | [],
  columns: TimetableSlot[][] = [],
): TimetableSlot[][] => {
  if (!slots.length) {
    return columns
  }

  const [currentSlot, ...otherSlots] = slots

  const matchingColumn = columns.find((column: any) =>
    canFitSlotIntoColumn(column, currentSlot),
  )

  if (matchingColumn) {
    matchingColumn.push(currentSlot)
  } else {
    columns.push([currentSlot])
  }
  return getSlotsPositions(otherSlots, columns)
}

/**
 * Greedy packing algorithm that recursively divides a list of slots into
 * columns/subcolumns. Uses canFitSlotIntoColumnByType to determine if
 * overlapping slots can be in the same column/subcolumn as one another.
 * @param slots Slots to be assigned to a column/subcolumn of the timetable.
 * @param columns The current column/subcolumns that have already been defined.
 *                It is used to persist column/subcolumn state during recursion;
 *                An empty array is typically used as the start value.
 * @return A 2D slot array, where each inner array is a column/subcolumn's slots.
 */
export const getSlotsPositionsByType = (
  slots: TimetableSlot[] | [],
  columns: TimetableSlot[][] = [],
): TimetableSlot[][] => {
  if (!slots.length) {
    return columns
  }

  const [currentSlot, ...otherSlots] = slots

  const matchingColumn = columns.find((column: any) =>
    canFitSlotIntoColumnByType(column, currentSlot),
  )

  if (matchingColumn) {
    matchingColumn.push(currentSlot)
  } else {
    columns.push([currentSlot])
  }
  return getSlotsPositionsByType(otherSlots, columns)
}

/**
 * Sorts TimetableSlots by duration in descending order (longest slot first).
 */
export const sortSlotsByDuration = (
  a: TimetableSlot,
  b: TimetableSlot,
): number => {
  const aStart = new Date(a.interval.from)
  const bStart = new Date(b.interval.from)
  const aEnd = new Date(a.interval.to)
  const bEnd = new Date(b.interval.to)
  let aDuration = aEnd.getTime() - aStart.getTime()
  let bDuration = bEnd.getTime() - bStart.getTime()
  if (Number.isNaN(aDuration)) aDuration = 0
  if (Number.isNaN(bDuration)) bDuration = 0
  return bDuration - aDuration
}

/**
 * Sorts TimetableSlots by start time in ascending order (earliest slot first).
 */
export const sortSlotsByStartTime = (
  a: TimetableSlot,
  b: TimetableSlot,
): number => {
  const aStart = new Date(a.interval.from).getTime()
  const bStart = new Date(b.interval.from).getTime()
  if (Number.isNaN(aStart) && Number.isNaN(bStart)) return 0
  if (Number.isNaN(aStart)) return 1
  if (Number.isNaN(bStart)) return -1
  return aStart - bStart
}

/**
 * Sorts TimetableSlots by event name, alphabetically.
 */
export const sortSlotsByName = (
  a: TimetableSlot,
  b: TimetableSlot,
  eventMap?: Record<string, TimetableEvent>,
): number => {
  if (eventMap) {
    const aId = getSlotId(a)
    const bId = getSlotId(b)
    const aName = eventMap[aId ?? -1]?.name ?? ''
    const bName = eventMap[bId ?? -1]?.name ?? ''
    if (aName < bName) return -1
    if (aName > bName) return 1
  }
  return 0
}

/**
 * Sorts TimetableSlots by slot ID, alphabetically.
 */
export const sortSlotsById = (a: TimetableSlot, b: TimetableSlot): number => {
  const aId = getSlotId(a)
  const bId = getSlotId(b)
  if ((aId ?? '') < (bId ?? '')) return -1
  if ((aId ?? '') > (bId ?? '')) return 1
  return 0
}

/**
 * Checks that a TimetableDateRange is a valid time range
 * (Dates objects are valid and in correct order)
 */
export const isTimeRangeValid = (range: TimetableDateRange): boolean =>
  range &&
  isValid(range.from.getTime()) &&
  isValid(range.to.getTime()) &&
  isBefore(range.from, range.to)

/**
 * Checks if 2 timetable time ranges overlap using the same date logic as slots.
 * Keep in parity with getSlotsAreOverlapping & getSlotsAreOverlappingWithType.
 */
export const isTimeRangeOverlapping = (
  a: TimetableDateRange,
  b: TimetableDateRange,
): boolean => {
  if (!isTimeRangeValid(a) || !isTimeRangeValid(b)) return false
  return isAfter(a.to, b.from) && isAfter(b.to, a.from)
}

/**
 * Subtracts time range B from A, returning the resulting range(s).
 */
export const subtractTimeRange = (
  a: TimetableDateRange,
  b: TimetableDateRange,
): TimetableDateRange[] => {
  if (!isTimeRangeValid(a) || !isTimeRangeValid(b)) return []
  if (!isTimeRangeOverlapping(a, b)) return [a]
  const results = []
  if (isAfter(b.from, a.from)) results.push({ from: a.from, to: b.from })
  if (isBefore(b.to, a.to)) results.push({ from: b.to, to: a.to })
  return results
}

/**
 * Converts a slot's string-based interval to a Date-based TimetableDateRange
 */
export const getTimeRangeForSlot = (
  slot: TimetableSlot,
): TimetableDateRange => ({
  from: new Date(slot.interval.from),
  to: new Date(slot.interval.to),
})

/**
 * Deterministic comparator for TimetableSlots - used to order slots along the timetable.
 */
export const sortSlotsForPositions = (
  a: TimetableSlot,
  b: TimetableSlot,
  eventMap?: Record<string, TimetableEvent>,
  reservedEventId?: string,
): number => {
  const slotTypePriority: Record<SlotType, number> = {
    [SlotType.APPOINTMENT]: 1,
    [SlotType.BUSY]: 3,
    [SlotType.NOT_AVAILABLE]: 4,
  }

  // If slot type is not found, sort slot last (rightmost)
  let aType = slotTypePriority[a.type] ?? 6
  let bType = slotTypePriority[b.type] ?? 6

  // If slots are reserved slots, adjust type sorting priority
  if (isBusySlotReserved(a, eventMap, reservedEventId)) aType -= 1
  if (isBusySlotReserved(b, eventMap, reservedEventId)) bType -= 1
  if (aType !== bType) {
    return aType - bType
  }

  // After slot type comparison, criteria is duration, startTime, name, then ID.
  const durationComparison = sortSlotsByDuration(a, b)
  if (durationComparison !== 0) return durationComparison
  const startComparison = sortSlotsByStartTime(a, b)
  if (startComparison !== 0) return startComparison
  const nameComparison = sortSlotsByName(a, b, eventMap)
  if (nameComparison !== 0) return nameComparison
  return sortSlotsById(a, b)
}

/**
 * Greedy packing algorithm that breaks a list of slots into columns/subcolumns.
 * Uses canFitSlotIntoColumnByTypeV2 to determine if overlapping slots can be
 * in the same column/subcolumn as one another. Contains some special case logic
 * to support stretching of appointments over reserved slots.
 *
 * eventMap & reservedEventId should be retired as part of CVC-34823
 *
 * @param slots Slots to be assigned to a column/subcolumn of the timetable.
 * @param eventMap A mapping of slot IDs to their respective TimetableEvent.
 *                 Event info is used to check if it's a reserved slot.
 * @param reservedEventId Event type ID for reserved slots
 * @return A 2D slot array, where each inner array is a column/subcolumn's slots.
 */
export const getSlotsPositionsByTypeV2 = (
  slots: TimetableSlot[],
  eventMap: Record<string, TimetableEvent>,
  reservedEventId: string,
): TimetableSlot[][] => {
  let remainingSlots = [...slots].sort((a, b) =>
    sortSlotsForPositions(a, b, eventMap, reservedEventId),
  )
  const columns: TimetableSlot[][] = []
  while (remainingSlots.length) {
    const [currentSlot, ...otherSlots] = remainingSlots
    remainingSlots = otherSlots

    const matchingColumn = columns.find(
      (column: TimetableSlot[], index: number) => {
        const canFit = canFitSlotIntoColumnByTypeV2(
          column,
          currentSlot,
          eventMap,
          reservedEventId,
        )
        // Special case for CVC-34541 affecting non-reserved BUSY slots.
        // Requirement to stretch appointments over reserved slots, but keep
        // non-reserved BUSY slots visible on the rightmost side so that they do
        // not get covered. This forces non-reserved BUSY slots to the right of
        // all reserved slots. This minimizes the odds of non-reserved slots
        // being covered up at the cost of more columns/subcolumns.
        const nonReservedBusy =
          isBusySlot(currentSlot) &&
          !isBusySlotReserved(currentSlot, eventMap, reservedEventId)
        if (canFit && nonReservedBusy) {
          const hasFutureConflicts = columns
            .slice(index + 1)
            .some(
              (futureColumn) =>
                !canFitSlotIntoColumnByTypeV2(
                  futureColumn,
                  currentSlot,
                  eventMap,
                  reservedEventId,
                ),
            )
          return !hasFutureConflicts
        }
        return canFit
      },
    )

    if (matchingColumn) {
      matchingColumn.push(currentSlot)
    } else {
      columns.push([currentSlot])
    }
  }
  return columns
}

/**
 * Utility function to determine if a legacy offset position qualifies as a full-width card.
 * In the old position system, a negative number means it's full-width.
 */
export const isSlotPositionHalfWidth = (
  offsetPosition: number | undefined,
): boolean => offsetPosition != null && offsetPosition >= 0

/**
 * Utility function to determine if a TimetableSlotPosition qualifies as a full-width card.
 * Rhapsody's various Event cards have unique styles when at "half width" (actually means it's not full-width).
 */
export const isSlotPositionHalfWidthV2 = (
  slotPosition: TimetableSlotPosition | undefined,
): boolean => {
  if (!slotPosition) return false
  return slotPosition.span === 1 && slotPosition.divisions > 1
}

/**
 * Calculates a slot's width as a percentage from a TimetableSlotPosition.
 * If divisions is 0 or falsy, it will assume that the denominator is 1.
 */
export const getSlotWidthPercentage = (
  slotPosition: TimetableSlotPosition | undefined,
): string => {
  if (!slotPosition) return '100%'
  return `${((slotPosition.span ?? 1) / (slotPosition.divisions || 1)) * 100}%`
}

/**
 * Calculates a slot's left offset in pixels using an offset number (old method).
 * Slots are full-width, or half of the column's default width if >1 subcolumn.
 * A non-negative offsetPosition typically means there's more than 1 subcolumn.
 * A nullish or negative offsetPosition returns 'auto' for legacy reasons, rather
 * than 0px, as that's the style originally used when passing undefined.
 */
export const getSlotOffsetPixels = (
  offsetPosition: number | undefined,
  defaultColumnWidth: number,
): string => {
  const fallbackColumnWidth = 208
  return offsetPosition == null || offsetPosition < 0
    ? `auto`
    : `${(offsetPosition * (defaultColumnWidth ?? fallbackColumnWidth)) / 2}px`
}

/**
 * Calculates a slot's left offset as a percentage from a TimetableSlotPosition.
 * If divisions is 0 or falsy, it will assume that the denominator is 1.
 */
export const getSlotOffsetPercentage = (
  slotPosition: TimetableSlotPosition | undefined,
): string => {
  if (!slotPosition) return '0%'
  return `${((slotPosition.offset ?? 0) / (slotPosition.divisions || 1)) * 100}%`
}

/**
 * Given a slot and positions array, calculate the appropriate offset
 * @param slot The slot to find the positioning information for
 * @param positions The positions array, representing which slots are in which sub-columns
 * @param isAppointmentReservedEnabled If the reserved block feature is enabled
 * @returns A number describing the starting sub-column of the slot, or -1 if
 *          the slot should span the full width of the column/subcolumn.
 */
export const getSlotOffsetPosition = (
  slot: TimetableSlot,
  positions: any[][],
  isAppointmentReservedEnabled = false,
) => {
  const positionIndex = positions.findIndex((position) =>
    position.includes(slot),
  )
  const otherPositions = R.remove(positionIndex, 1, positions)

  const hasConflictingSlotsInOtherPositions = otherPositions.some((position) =>
    position.some(
      (otherSlot) =>
        getSlotsAreOverlapping(slot, otherSlot) &&
        (!isAppointmentReservedEnabled ||
          isBusySlot(otherSlot) === isBusySlot(slot)),
    ),
  )

  return hasConflictingSlotsInOtherPositions ? positionIndex : -1
}

/**
 * Basic TimetableSlotPosition for a full-width slot, if no positioning is available.
 */
export const getFullwidthSlotPosition = (): TimetableSlotPosition => ({
  offset: 0,
  span: 1,
  divisions: 1,
})

/**
 * Given a slot and positions array, calculate the appropriate TimetableSlotPosition
 * @param slot - The slot to find the positioning information for
 * @param positions - The positions array, representing which slots are in which sub-columns
 * @returns A TimetableSlotPosition describing the starting sub-column of the slot and it's width
 */
export const getSlotOffsetPositionV2 = (
  slot: TimetableSlot,
  positions: TimetableSlot[][],
): TimetableSlotPosition => {
  const positionIndex = positions.findIndex((position) =>
    position.includes(slot),
  )

  if (positionIndex < 0) {
    return getFullwidthSlotPosition()
  }

  const otherPositions = R.remove(positionIndex, 1, positions)

  const hasConflictingSlotsInOtherPositions = otherPositions.some((position) =>
    position.some((otherSlot) => getSlotsAreOverlapping(slot, otherSlot)),
  )

  const offset = hasConflictingSlotsInOtherPositions ? positionIndex : 0

  return {
    offset,
    span: 1,
    divisions: positions.length,
  }
}

/**
 * Checks if a slot overlaps with any slots in the provided list.
 * Simple check split out to reduce cyclomatic complexity.
 */
const hasOverlapWithAnySlot = (
  slot: TimetableSlot,
  otherSlots: TimetableSlot[],
): boolean =>
  otherSlots.some((otherSlot) => getSlotsAreOverlapping(slot, otherSlot))

/**
 * Given a slot and a list of other slots, find all that fit in the same overlap
 * group.
 */
export const getOverlapGroupForSlot = (
  initialSlot: TimetableSlot,
  slots: TimetableSlot[],
): [TimetableOverlapGroup, TimetableSlot[]] => {
  const overlappingSlots = [initialSlot]
  let remainingSlots: TimetableSlot[] = [...slots]
  let minFrom: Date = new Date(initialSlot.interval.from)
  let maxTo: Date = new Date(initialSlot.interval.to)
  let foundOverlap: boolean

  // Iterate over combinations, and stop when no more overlaps are found
  do {
    foundOverlap = false
    remainingSlots = remainingSlots.filter(
      (slot) => !overlappingSlots.includes(slot),
    )
    for (let index = 0; index < remainingSlots.length; index += 1) {
      const candidateSlot = remainingSlots[index]
      if (hasOverlapWithAnySlot(candidateSlot, overlappingSlots)) {
        overlappingSlots.push(candidateSlot)
        const slotFrom: Date = new Date(candidateSlot.interval.from)
        const slotTo: Date = new Date(candidateSlot.interval.to)
        if (slotFrom < minFrom) minFrom = slotFrom
        if (slotTo > maxTo) maxTo = slotTo
        foundOverlap = true
        break
      }
    }
  } while (foundOverlap)

  const overlapGroup: TimetableOverlapGroup = {
    slots: overlappingSlots,
    interval: {
      from: minFrom,
      to: maxTo,
    },
  }
  return [overlapGroup, remainingSlots]
}

/**
 * Given a list of slots, divide them into overlap groups.
 */
export const getOverlapGroups = (
  slots: TimetableSlot[],
): TimetableOverlapGroup[] => {
  const overlapGroups: TimetableOverlapGroup[] = []
  let groupableSlots: TimetableSlot[] = slots
  while (groupableSlots.length) {
    const [rootSlot, ...otherSlots] = groupableSlots
    const [overlapGroup, remainingSlots] = getOverlapGroupForSlot(
      rootSlot,
      otherSlots,
    )
    overlapGroups.push(overlapGroup)
    groupableSlots = remainingSlots
  }
  return overlapGroups
}

/**
 * Given a list of slots, split the slots by their SlotType. If eventMap &
 * reservedEventId are provided, it will treat reserved slots separately from
 * other BUSY slots.
 */
export const getSlotsBySlotType = (
  slots: TimetableSlot[],
  eventMap?: Record<string, TimetableEvent>,
  reservedEventId?: string,
): Map<SlotType | 'RESERVED', TimetableSlot[]> => {
  const mapping: Map<SlotType | 'RESERVED', TimetableSlot[]> = new Map()
  slots.forEach((slot) => {
    const type: SlotType | 'RESERVED' = isBusySlotReserved(
      slot,
      eventMap,
      reservedEventId,
    )
      ? 'RESERVED'
      : slot.type
    const typeMapping = mapping.get(type) ?? []
    mapping.set(type, [...typeMapping, slot])
  })
  return mapping
}

/**
 * Checks if a TimetableSlot is fully overlapped by any number of other slots by
 * finding differences in their time ranges.
 */
export const isSlotFullyOverlapped = (
  slot: TimetableSlot,
  possibleOverlaps: TimetableSlot[],
): boolean => {
  const remainingTime = possibleOverlaps.reduce(
    (ranges, otherSlot) => {
      const otherRange = getTimeRangeForSlot(otherSlot)
      return ranges.flatMap((range: TimetableDateRange) =>
        subtractTimeRange(range, otherRange),
      )
    },
    [getTimeRangeForSlot(slot)],
  )
  // If repeated subtractions results in 0 time ranges, it's fully overlapped
  return remainingTime.length === 0
}

/**
 * Given two "shapes" for different slot groups, calculates how much the first
 * can scale in order to fit the target "shape".
 * @param scaledShape The "shape" of the slots we want to scale to fit
 * @param targetShape The "shape" of the area we want to cover with scaledShape
 * @return The largest factor scaledShape can be stretched by while staying
 *         within the bounds of targetShape. Returns -1 if there's no overlap
 *         between the two shapes - making it impossible to calculate a minimum.
 */
export const getMinimumScaleRatioForSlotShape = (
  scaledShape: TimetableSlotBoundingShape,
  targetShape: TimetableSlotBoundingShape,
): number => {
  let minRatio = -1
  scaledShape.bounds.forEach((scaledBounds: TimetableSlotBounds) => {
    targetShape.bounds.forEach((candidateBounds: TimetableSlotBounds) => {
      const overlaps = isTimeRangeOverlapping(
        scaledBounds.interval,
        candidateBounds.interval,
      )
      if (overlaps) {
        const scaleRatio =
          candidateBounds.subcolumnCount / scaledBounds.subcolumnCount
        minRatio = minRatio < 0 ? scaleRatio : Math.min(minRatio, scaleRatio)
      }
    })
  })
  return minRatio
}

/**
 * Builds a TimetableSlotBoundingShape for a list of slots. The return value of
 * this function can be used to later calculate how much slots can be stretched
 * in relation to another shape. The generated shape is convex, which in this
 * case means there are no gaps represented on the top and bottom edges.
 *
 * For an example of how a shape is structured, imagine the following slots:
 * - Slot A (first subcolumn): 1:00-2:00
 * - Slot B (second subcolumn): 1:15-1:45
 *
 * The shape of this could be represented as 3 TimetableSlotBounds:
 * - 1:00-1:15 => 1 subcolumn
 * - 1:15-1:45 => 2 subcolumns
 * - 1:45-2:00 => 1 subcolumn
 *
 * @param slots The slots that are having their shape calculated.
 * @param subcolumns The subcolumn assignments for a set of slots - typically
 *                   from getSlotsPositionsV2 or a similar function.
 * @return An TimetableSlotBoundingShape, where each TimetableSlotBounds's
 *         interval is mutually exclusive from the other elements', forming the
 *         convex "shape" of all slots in the subcolumns array.
 */
export const getSlotBoundingShape = (
  slots: TimetableSlot[],
  subcolumns: TimetableSlot[][],
): TimetableSlotBoundingShape => {
  if (!subcolumns?.length) return { bounds: [] }
  const slotSet: Set<TimetableSlot> = new Set(slots)
  const bounds = subcolumns
    .reduce(
      (
        rawBounds: TimetableSlotBounds[][],
        subcolumn: TimetableSlot[],
        subcolumnIndex: number,
      ) => {
        rawBounds.push([])
        // Converts slots into time ranges, and resolves earlier conflicts
        subcolumn.forEach((slot) => {
          if (slotSet.has(slot)) {
            const slotInterval = getTimeRangeForSlot(slot)
            rawBounds[subcolumnIndex].push({
              interval: slotInterval,
              subcolumnCount: subcolumnIndex + 1,
            })
            // Iterates back over earlier slots to update time ranges
            // Ensures all time ranges in the returned shape are mutually exclusive
            for (let prevIndex = 0; prevIndex < subcolumnIndex; prevIndex++) {
              rawBounds[prevIndex] = rawBounds[prevIndex].flatMap(
                (previousBounds: TimetableSlotBounds) =>
                  subtractTimeRange(previousBounds.interval, slotInterval).map(
                    (interval) => ({
                      interval,
                      subcolumnCount: prevIndex + 1,
                    }),
                  ),
              )
            }
          }
        })
        return rawBounds
      },
      [] as TimetableSlotBounds[][],
    )
    .flat()
  return { bounds }
}

/**
 * Builds a TimetableSlotBoundingShape for space NOT occupied by non-reserved
 * BUSY slots - similar to getSlotBoundingShape, but not focuses on available
 * space instead of just space taken up by slots.
 *
 * For an example of how a shape is structured, imagine the following slots:
 * - Slot A (first subcolumn):  Appointment 1:00-3:00
 * - Slot B (second subcolumn): Appointment 1:30-2:30
 * - Slot C (third subcolumn):  Busy 1:00-2:00
 *
 * The non-busy shape of this could be represented as 2 TimetableSlotBounds:
 * - 1:00-2:00 => 2 subcolumn
 * - 2:00-3:00 => 3 subcolumns
 *
 * @param overlapGroup The overlap group factored into creating the bounding shape.
 * @param subcolumns The subcolumn assignments for a set of slots - typically
 *                   from getSlotsPositionsV2 or a similar function.
 * @param eventMap Used to tell reserved BUSY from other BUSY
 * @param reservedEventId Event type id for reserved slots
 * @return An TimetableSlotBoundingShape, where each TimetableSlotBounds's
 *         interval is mutually exclusive from the other elements', forming the
 *         convex "shape" of all slots in the subcolumns array.
 */
export const getNonBusyBoundingShape = (
  overlapGroup: TimetableOverlapGroup,
  subcolumns: TimetableSlot[][],
  eventMap: Record<string, TimetableEvent>,
  reservedEventId: string,
): TimetableSlotBoundingShape => {
  if (!subcolumns?.length) return { bounds: [] }
  const slotSet = new Set(overlapGroup.slots)
  const groupBounds: TimetableSlotBounds = {
    interval: overlapGroup.interval,
    subcolumnCount: subcolumns.length,
  }
  const bounds = subcolumns.reduceRight(
    (
      rawBounds: TimetableSlotBounds[],
      subcolumn: TimetableSlot[],
      subcolumnIndex: number,
    ) => {
      subcolumn.forEach((slot) => {
        if (slotSet.has(slot)) {
          const slotInterval = getTimeRangeForSlot(slot)
          const isNonReservedBusy =
            isBusySlot(slot) &&
            !isBusySlotReserved(slot, eventMap, reservedEventId)
          if (isNonReservedBusy) {
            // Busy slots are used to reduce the number of subcolumns in a given time range
            const slotBounds: TimetableSlotBounds[] = [
              {
                interval: slotInterval,
                subcolumnCount: subcolumnIndex, // If busy, the current subcolumn is occupied
              },
            ]
            rawBounds = rawBounds
              .flatMap((existingBounds) =>
                subtractTimeRange(existingBounds.interval, slotInterval).map(
                  (interval) => ({
                    interval,
                    subcolumnCount: existingBounds.subcolumnCount,
                  }),
                ),
              )
              .concat(slotBounds)
          }
        }
      })
      return rawBounds
    },
    [groupBounds],
  )
  return { bounds }
}

/**
 * Given a list of Schedules, calculate the positioning of all slots.
 * This is the newer method of calculating TimetableSlotPositions, which supports:
 *
 * 1. Breaking subcolumn calculations into overlap groups, so that a time with
 *    many overlaps doesn't impact the size of slots in areas with few overlaps.
 * 2. Stretching appointment/reserved slots to match widths where able, ensuring
 *    that reserved slots get covered with appointments (part of CVC-34541).
 * 3. Adjusted subcolumn assignment logic, to ensure that BUSY slots do not
 *    interrupt stretches of reserved slots that we want to cover.
 *
 * This is intended to act as a departure from the rigid grid-based positioning
 * used previously, where slots were only able to take up a single sub-column or
 * all subcolumns, leading to cases where reserved slots were not covered.
 * @param slots All slots that need positions calculated
 * @param isAppointmentReservedEnabled Is RESERVED_BLOCK feature enabled
 * @param eventMap Mapping of slot IDs to their respective TimetableEvent
 * @param reservedEventId Event type ID for reserved slots
 * @return A mapping of TimetableSlots to TimetableSlotPosition, for O(1) lookup
 */
export const getSlotPositionsUsingOverlapGroups = (
  slots: TimetableSlot[],
  isAppointmentReservedEnabled: boolean,
  eventMap: Record<string, TimetableEvent>,
  reservedEventId: string,
): Map<TimetableSlot, TimetableSlotPosition> => {
  const sortedSlots = [...slots].sort((a, b) =>
    sortSlotsForPositions(a, b, eventMap, reservedEventId),
  )
  const slotPositions: Map<TimetableSlot, TimetableSlotPosition> = new Map()

  // First finds "overlap groups" - clusters of slots that overlap at least 1 other slot.
  const overlapGroups = getOverlapGroups(sortedSlots)

  // Once we have the overlap groups, we determine the base-level positions for all slots.
  // Positions is a greedy subcolumn assignment assuming each slot is 1 subcolumn wide.
  const subcolumnsByOverlapGroup: TimetableSlot[][][] = []
  overlapGroups.forEach((overlapGroup) => {
    const positions = isAppointmentReservedEnabled
      ? getSlotsPositionsByTypeV2(overlapGroup.slots, eventMap, reservedEventId)
      : getSlotsPositions(overlapGroup.slots)
    subcolumnsByOverlapGroup.push(positions)
  })

  // Finally with both the groups and subcolumns, we can generate TimetableSlotPositions.
  overlapGroups.forEach((group: TimetableOverlapGroup, groupIndex: number) => {
    const subcolumns = subcolumnsByOverlapGroup[groupIndex]
    group.slots.forEach((slot: TimetableSlot) => {
      slotPositions.set(slot, getSlotOffsetPositionV2(slot, subcolumns))
    })

    // Slot stretching happens at this phase too.
    // Overlap groups don't guarantee all slots of the same type are touching one
    // another, as such we need to make type-specific overlap groups within the
    // larger overlap groups we've already defined.
    if (isAppointmentReservedEnabled) {
      const nonBusyShape = getNonBusyBoundingShape(
        group,
        subcolumns,
        eventMap,
        reservedEventId,
      )
      const groupSlotsByType = getSlotsBySlotType(
        group.slots,
        eventMap,
        reservedEventId,
      )
      const appointmentSubgroups = getOverlapGroups(
        groupSlotsByType.get(SlotType.APPOINTMENT) ?? [],
      )
      const reservedSubgroups = getOverlapGroups(
        groupSlotsByType.get('RESERVED') ?? [],
      )
      const scaleableSubgroups = [...appointmentSubgroups, ...reservedSubgroups]
      // Once we have the overlap groups & shapes for appointments/reserved, we
      // can leverage them to find the largest amount to scale each subgroup
      scaleableSubgroups.forEach((subgroup: TimetableOverlapGroup) => {
        const subgroupShape = getSlotBoundingShape(subgroup.slots, subcolumns)
        const minRatio = getMinimumScaleRatioForSlotShape(
          subgroupShape,
          nonBusyShape,
        )
        // Do not scale if below 1, we only want to scale UP
        if (minRatio > 1) {
          subgroup.slots.forEach((slot) => {
            const defaultPosition = slotPositions.get(slot)
            if (defaultPosition) {
              slotPositions.set(slot, {
                ...defaultPosition,
                offset: defaultPosition.offset * minRatio,
                span: defaultPosition.span * minRatio,
              })
            }
          })
        }
      })
    }
  })

  return slotPositions
}

export const getTodayAppointmentsByType = (
  schedules: BoardingSchedule[],
  appointmentsMap: Record<string, TimetableEvent>,
  types: string[],
  getDate: (slot: TimetableSlot) => string,
) => {
  const items = R.pluck('items', schedules)
  const slots = R.flatten(R.pluck('slots', R.flatten(items)))
  const checkOutTodaySlots = slots.filter((slot) =>
    moment(getDate(slot)).isSame(new Date(), 'day'),
  )

  return checkOutTodaySlots
    .map((slot) => appointmentsMap[getSlotId(slot)!])
    .filter(
      (appointment) =>
        appointment.state && types.includes(appointment.state.id),
    )
}

/**
 * Given a list of slots, calculate the necessary positioning values and column
 * size for the scheduler view as a TimetableColumnLayout object.
 * @param slots The slots to be positioned in the scheduler view
 * @param isAppointmentReservedEnabled If APPOINTMENT_RESERVED feature is on
 * @param isImprovedSlotOverlapsEnabled If IMPROVED_SLOT_OVERLAPS feature is on
 * @param eventMap Mapping of slot IDs to their respective TimetableEvent
 * @param reservedEventId Event type ID for reserved slots
 */
export const getScheduleViewColumnLayout = (
  slots: TimetableSlot[],
  isAppointmentReservedEnabled: boolean,
  isImprovedSlotOverlapsEnabled: boolean,
  eventMap: Record<string, TimetableEvent>,
  reservedEventId: string,
): TimetableColumnLayout => {
  const layout: TimetableColumnLayout = { maxOverlaps: 1 }
  if (isImprovedSlotOverlapsEnabled) {
    // Newer method using TimetableSlotPositions
    layout.slotPositions = getSlotPositionsUsingOverlapGroups(
      slots,
      isAppointmentReservedEnabled,
      eventMap,
      reservedEventId,
    )
    layout.maxOverlaps = Array.from(layout.slotPositions.values()).reduce(
      (max: number, position: TimetableSlotPosition) =>
        Math.max(max, position.divisions),
      0,
    )
  } else {
    // Older method using exclusive subcolumn assignment
    layout.subcolumns = isAppointmentReservedEnabled
      ? getSlotsPositionsByType(slots)
      : getSlotsPositions(slots)
    layout.maxOverlaps = layout.subcolumns.length
  }
  return layout
}

/**
 * Uses isSlotFullyOverlapped() to filter out any reserved slots that are fully
 * overlapped by appointment slots. Useful for allocating more real estate to
 * reserved blocks that will be still visible.
 * @param slots The slot list to be filtered
 * @param eventMap Map of slot IDs to events, used to distinguish if a slot is Reserved
 * @param reservedEventId ID of reserved event type, used to distinguish if a slot is Reserved
 * @return A list of TimetableSlots where all remaining reserved slots are not
 *         fully overlapped by appointment slots.
 */
export const filterFullyOverlappedReservedSlots = (
  slots: TimetableSlot[],
  eventMap: Record<string, TimetableEvent>,
  reservedEventId: string,
): TimetableSlot[] => {
  const appointmentSlots = slots.filter(isAppointmentSlot)
  return slots.filter(
    (slot) =>
      !isBusySlotReserved(slot, eventMap, reservedEventId) ||
      !isSlotFullyOverlapped(slot, appointmentSlots),
  )
}

export const getAppointmentColorState = (state: string | undefined) =>
  AppointmentStateColorMap[state ?? 'DEFAULT'] ||
  AppointmentStateColorMap.DEFAULT

type GetAppointmentWarningLabel = {
  from?: string | Nil
  stateName?: string
  to?: string | Nil
}

const WARNING_THRESHOLD = 10 // minutes
const WAITING_WARNING_THRESHOLD = 15 // minutes

/*
  Status in (Scheduled, Contacted, Confirmed, Rescheduled)
  > 10 minutes after appointment start datetime
*/
const isAppointmentLate = ({ from, stateName }: GetAppointmentWarningLabel) => {
  // TODO: leave only Scheduled and Rescheduled status for now
  const lateCandidates = ['Scheduled', 'Rescheduled'] // ['Scheduled', 'Contacted', 'Confirmed', 'Rescheduled']
  const diff = moment().diff(moment(from), 'minute')
  const isLate =
    stateName && lateCandidates.includes(stateName) && diff > WARNING_THRESHOLD
  return { isLate, diff }
}

/*
  Status in (Arrived)
  > 15 minutes after appointment start datetime
*/
const isAppointmentWaiting = ({
  from,
  stateName,
}: GetAppointmentWarningLabel) => {
  const lateCandidates = ['Arrived']
  const diff = moment().diff(moment(from), 'minute')
  const isWaiting =
    stateName &&
    lateCandidates.includes(stateName) &&
    diff > WAITING_WARNING_THRESHOLD
  return { isWaiting, diff }
}

/*
  Status in (Admitted, Hospitalized, In progress)
  > 10 minutes after appointment end datetime
*/
const isAppointmentBehind = ({ to, stateName }: GetAppointmentWarningLabel) => {
  // TODO: remove behind options for now
  const behindCandidates = [] as any[] // ['Admitted', 'Hospitalized', 'In progress']
  const diff = moment().diff(moment(to), 'minute')
  const isBehind =
    stateName &&
    behindCandidates.includes(stateName) &&
    diff > WARNING_THRESHOLD
  return { isBehind, diff }
}

const humanizeDiff = (diff: number) => {
  const m = diff % 60
  const h = Math.floor((diff - m) / 60)
  const d = Math.floor(h / 24)

  if (d >= 1) {
    return i18n.t('Time:FORMAT.DAYS', { days: d })
  }

  return h > 0
    ? i18n.t('Time:FORMAT.HOURS', { hours: h, minutes: m })
    : i18n.t('Time:FORMAT.MINUTES', { minutes: m })
}

export const getAppointmentWarningLabel = ({
  from,
  to,
  stateName,
}: GetAppointmentWarningLabel) => {
  const { isLate, diff: lateDiff } = isAppointmentLate({ from, to, stateName })
  const { isBehind, diff: behindDiff } = isAppointmentBehind({
    from,
    to,
    stateName,
  })
  const { isWaiting, diff: waitingDiff } = isAppointmentWaiting({
    from,
    to,
    stateName,
  })

  return isWaiting
    ? i18n.t('Time:LABEL.WAITING', { time: humanizeDiff(waitingDiff) })
    : isLate
      ? i18n.t('Time:LABEL.LATE', { time: humanizeDiff(lateDiff) })
      : isBehind
        ? i18n.t('Time:LABEL.BEHIND', { time: humanizeDiff(behindDiff) })
        : ''
}

export const getAppointmentTimeRange = (appointment: TimetableEvent) => ({
  startTime: moment(appointment?.scheduledStartDatetime),
  endTime: moment(appointment?.scheduledEndDatetime),
})

export const getHourAppointmentRange = (appointment: TimetableEvent) => {
  const [dateOne, dateTwo] = R.values(getAppointmentTimeRange(appointment))
  return formatHourRange(dateOne, dateTwo)
}

export const getUrlTimetableDate = () =>
  getUrlSearchParam('date', window.location.search) || undefined

export const checkEventAppointmentType = (
  appointment: TimetableEvent | Nil,
  AppointmentType: AppointmentEventType,
) => {
  const typeId = appointment?.typeId || appointment?.type?.id
  return typeId && R.includes(typeId, R.pluck('id', AppointmentType.subTypes))
}

/**
 * Deterministic comparator for TimetableSlots - used to determine render order.
 * Elements that are sorted later will be rendered on top of other slots.
 * APPOINTMENT slots are sorted later to ensure they're placed over BUSY slots.
 * Slots are sorted by start time before duration, for better render ordering.
 * @param a - First TimetableSlot being compared
 * @param b - Second TimetableSlot being compared
 * @param eventMap - Mapping of slot IDs to events, used to distinguish if a slot is Reserved.
 * @param reservedEventId - ID of reserved event type, used to distinguish if a slot is Reserved.
 * @return <1 if a comes before b, >1 if a comes after b, 0 if a & b are equal.
 */
export const sortSlotsForRendering = (
  a: TimetableSlot,
  b: TimetableSlot,
  eventMap?: Record<string, TimetableEvent>,
  reservedEventId?: string,
): number => {
  const slotTypePriority: Record<SlotType, number> = {
    [SlotType.NOT_AVAILABLE]: 1,
    [SlotType.BUSY]: 3,
    [SlotType.APPOINTMENT]: 4,
  }

  // If slot type is not found, sort slot first (rendered at bottom)
  let aType = slotTypePriority[a.type] ?? 0
  let bType = slotTypePriority[b.type] ?? 0

  // If slots are reserved slots, adjust type sorting priority
  if (isBusySlotReserved(a, eventMap, reservedEventId)) aType -= 1
  if (isBusySlotReserved(b, eventMap, reservedEventId)) bType -= 1
  if (aType !== bType) {
    return aType - bType
  }

  // After slot type comparison, criteria is start time, duration, name, then ID.
  const startComparison = sortSlotsByStartTime(a, b)
  if (startComparison !== 0) return startComparison
  const durationComparison = sortSlotsByDuration(a, b)
  if (durationComparison !== 0) return durationComparison
  const nameComparison = sortSlotsByName(a, b, eventMap)
  if (nameComparison !== 0) return nameComparison
  return sortSlotsById(a, b)
}
